import * as React from 'react';
import { FocusScope } from '@react-aria/focus';
import { usePopper } from 'react-popper';
import { canTrulyHover, isNode } from '../../dom';
import { Layer } from '../Helper';
import { PopoverArrow } from './PopoverArrow';
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
const defaultModifiers = [{
  name: 'offset',
  options: {
    offset: [0, 7]
  }
}];
function PopoverContainer({
  visible = false,
  placement = 'auto',
  behaviour = 'hover',
  role = 'tooltip',
  modifiers = defaultModifiers,
  id,
  renderReference,
  children,
  disabled,
  interactive,
  onShow,
  onHide
}) {
  const enterTimer = React.useRef();
  const leaveTimer = React.useRef();
  const hoverCapable = React.useMemo(canTrulyHover, []);
  const [referenceElement, setReferenceElement] = React.useState(null);
  const [popperElement, setPopperElement] = React.useState(null);
  const [arrowElement, setArrowElement] = React.useState(null);
  const {
    styles,
    attributes
  } = usePopper(referenceElement, popperElement, {
    placement,
    modifiers: [...modifiers, {
      name: 'arrow',
      options: {
        element: arrowElement
      }
    }]
  });
  const [state, setState] = React.useState({
    visible
  });
  React.useEffect(() => () => {
    clearTimeout(enterTimer.current);
    clearTimeout(leaveTimer.current);
  }, []);
  const show = evt => {
    if (state.visible) {
      return;
    }
    setState({
      visible: true
    });
    if (typeof onShow === 'function') {
      onShow(evt);
    }
  };
  const hide = React.useCallback(evt => {
    if (!state.visible) {
      return;
    }
    setState({
      visible: false
    });
    if (typeof onHide === 'function') {
      onHide(evt);
    }
  }, [state.visible, onHide]);
  const prevDisabled = React.useRef(disabled);
  React.useEffect(() => {
    if (disabled && disabled !== prevDisabled.current) {
      hide();
    }
    prevDisabled.current = disabled;
  }, [disabled, hide]);
  const handleClickOutside = React.useCallback(evt => {
    if (isNode(referenceElement) && referenceElement.contains(evt.target) || isNode(popperElement) && popperElement.contains(evt.target)) {
      return;
    }
    hide(evt);
  }, [hide, popperElement, referenceElement]);
  React.useEffect(() => {
    if (behaviour !== 'click') {
      return;
    }
    document.addEventListener('click', handleClickOutside, false);
    return () => {
      document.removeEventListener('click', handleClickOutside, false);
    };
  }, [behaviour, handleClickOutside]);
  const handleEnter = evt => {
    clearTimeout(enterTimer.current);
    clearTimeout(leaveTimer.current);
    enterTimer.current = setTimeout(() => {
      show(evt);
    }, 200);
  };
  const handleLeave = evt => {
    clearTimeout(enterTimer.current);
    clearTimeout(leaveTimer.current);
    leaveTimer.current = setTimeout(() => {
      hide(evt);
    }, 200);
  };
  const handleFocus = evt => {
    show(evt);
  };
  const handleBlur = evt => {
    hide(evt);
  };
  const handleKeyDown = evt => {
    if (evt.key === 'Escape') {
      hide(evt);
    }
  };
  const handleClick = evt => {
    evt.preventDefault();
    const handler = state.visible ? hide : show;
    handler(evt);
  };
  const triggerProps = (() => {
    if (disabled) {
      return {};
    }
    if (behaviour === 'hover' && hoverCapable) {
      return {
        'aria-haspopup': role,
        'aria-describedby': state.visible ? id : undefined,
        onMouseOver: handleEnter,
        onMouseLeave: handleLeave,
        onKeyDown: handleKeyDown,
        onFocus: handleFocus,
        onBlur: handleBlur
      };
    }
    return {
      'aria-haspopup': role,
      'aria-expanded': state.visible,
      'aria-controls': state.visible ? id : undefined,
      onClick: handleClick
    };
  })();
  const wrapperProps = interactive && behaviour === 'hover' && hoverCapable ? {
    onMouseOver: handleEnter,
    onMouseLeave: handleLeave
  } : {};
  const popoverProps = behaviour === 'click' ? {
    dismissible: true,
    onDismiss: hide,
    onKeyDown: handleKeyDown
  } : {};
  const focusProps = behaviour === 'click' ? {
    contain: true,
    restoreFocus: true,
    autoFocus: true
  } : {};
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [renderReference(mergeProps => ({
      title: '',
      innerRef: setReferenceElement,
      ...triggerProps,
      ...mergeProps,
      id
    }), state.visible), state.visible && /*#__PURE__*/_jsx(Layer, {
      children: /*#__PURE__*/_jsx("div", {
        ref: setPopperElement,
        style: styles.popper,
        ...attributes.popper,
        ...wrapperProps,
        children: /*#__PURE__*/_jsx(FocusScope, {
          ...focusProps,
          children: children(mergeProps => ({
            id,
            arrow: /*#__PURE__*/_jsx(PopoverArrow, {
              ref: node => setArrowElement(node),
              style: styles.arrow
            }),
            ...popoverProps,
            ...mergeProps
          }), state.visible)
        })
      })
    })]
  });
}
export { PopoverContainer };